import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { FileText, Download, X, Calculator, CheckCircle, Eye } from "lucide-react";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;

const fmt = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;

export default function EmployeePayroll() {
  const { T } = useTheme();
  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, fontFamily:"'Outfit',sans-serif", overflow:"hidden" };

  const INV_SECTIONS = [
    { key:"section_80c",      label:"Section 80C",                    desc:"LIC, PPF, ELSS, NSC, ULIP, tuition fees, home loan principal", max:150000, color:T.cyan },
    { key:"section_80d",      label:"Section 80D",                    desc:"Health insurance premium for self, spouse, children",          max:25000,  color:T.green },
    { key:"house_rent_paid",  label:"HRA — House Rent Paid / month",  desc:"Monthly rent paid (annual = ×12 for calculation)",             max:null,   color:T.amber },
    { key:"section_80e",      label:"Section 80E",                    desc:"Education loan interest repayment (no limit)",                 max:null,   color:T.purple },
    { key:"section_80g",      label:"Section 80G",                    desc:"Donations to approved charitable institutions",                max:null,   color:"#A855F7" },
    { key:"other_deductions", label:"Other Deductions",               desc:"Any other eligible deductions",                               max:null,   color:T.muted },
  ];

  const userData   = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const platformId = userData.id;
  const token      = sessionStorage.getItem("platform_token") || "";
  const ax         = { headers:{ Authorization:`Bearer ${token}` } };

  const [payslips,       setPayslips]       = useState([]);
  const [investment,     setInvestment]     = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [invOpen,        setInvOpen]        = useState(false);
  const [slipOpen,       setSlipOpen]       = useState(false);
  const [selSlip,        setSelSlip]        = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [computing,      setComputing]      = useState(false);
  const [computeResult,  setComputeResult]  = useState(null);
  const [savedMsg,       setSavedMsg]       = useState("");
  const [downloading,    setDownloading]    = useState(null); // payslip id being downloaded

  const CURR_FY = "2025-26";
  const [form, setForm] = useState({
    financial_year:CURR_FY, regime:"New",
    section_80c:0, section_80d:0, house_rent_paid:0,
    section_80e:0, section_80g:0, other_deductions:0,
  });

  const load = async () => {
    try {
      setLoading(true);
      const [pRes, iRes] = await Promise.all([
        axios.get(`${API}/employee-portal/payslips?platform_user_id=${platformId}`, ax),
        axios.get(`${API}/employee-portal/investment-declaration?platform_user_id=${platformId}`, ax),
      ]);
      setPayslips(pRes.data || []);
      const inv = iRes.data;
      if (inv.declared) {
        setInvestment(inv);
        setForm({
          financial_year:  inv.financial_year,   regime:           inv.regime,
          section_80c:     inv.section_80c,       section_80d:      inv.section_80d,
          house_rent_paid: inv.house_rent_paid,   section_80e:      inv.section_80e,
          section_80g:     inv.section_80g,       other_deductions: inv.other_deductions,
        });
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (platformId) load(); }, [platformId]);

  // ── PDF download ──────────────────────────────────────────────
  const downloadPDF = async (ps) => {
    setDownloading(ps.id);
    try {
      const res = await axios.post(
        `${API}/payslips/${ps.id}/pdf`,
        {},                              // empty body — company name resolved from token
        { ...ax, responseType:"blob" }
      );
      const url  = window.URL.createObjectURL(new Blob([res.data], { type:"application/pdf" }));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", `Payslip_${ps.month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch(e) {
      console.error("PDF download failed:", e);
      alert("Could not download PDF. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  // ── View PDF in new tab ───────────────────────────────────────
  const viewPDF = async (ps) => {
    setDownloading(`view-${ps.id}`);
    try {
      const res = await axios.post(
        `${API}/payslips/${ps.id}/pdf`,
        {},
        { ...ax, responseType:"blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type:"application/pdf" }));
      window.open(url, "_blank");
    } catch(e) {
      console.error("PDF view failed:", e);
      alert("Could not open PDF. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const handleCompute = async () => {
    setComputing(true); setComputeResult(null);
    try {
      const r = await axios.post(
        `${API}/employee-portal/investment-declaration?platform_user_id=${platformId}`,
        { ...form, ...Object.fromEntries(Object.entries(form).map(([k,v])=>[k,typeof v==="string"&&k!=="financial_year"&&k!=="regime"?parseFloat(v)||0:v])) },
        ax
      );
      setComputeResult(r.data);
    } catch(e) { alert(e.response?.data?.detail || "Error computing tax"); }
    finally { setComputing(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, ...Object.fromEntries(INV_SECTIONS.map(s=>[s.key, parseFloat(form[s.key])||0])) };
      const r = await axios.post(
        `${API}/employee-portal/investment-declaration?platform_user_id=${platformId}`,
        payload, ax
      );
      setSavedMsg(`✅ Saved! Monthly TDS: ${fmt(r.data.monthly_tds)} · Annual Tax: ${fmt(r.data.annual_tax)}`);
      setTimeout(() => setSavedMsg(""), 5000);
      setInvOpen(false); load();
    } catch(e) { alert(e.response?.data?.detail || "Error saving"); }
    finally { setSaving(false); }
  };

  const getComps = (ps) => {
    try { return JSON.parse(typeof ps.components==="string" ? ps.components : JSON.stringify(ps.components||{})); }
    catch { return {}; }
  };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:22 }}>
        <div>
          <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>PAYROLL</div>
          <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>My Payroll</h2>
          <p style={{ fontSize:13, color:T.muted, margin:0 }}>Payslips and investment declarations</p>
        </div>
        <button onClick={() => setInvOpen(true)}
          style={{ display:"inline-flex", alignItems:"center", gap:7, background:`linear-gradient(135deg,${T.cyan},${T.purple})`, border:"none", borderRadius:8, color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600, padding:"10px 18px", cursor:"pointer" }}>
          <Calculator size={14}/> {investment?.declared ? "Update Investment" : "Submit Investment"}
        </button>
      </div>

      {savedMsg && (
        <div style={{ ...card, padding:"12px 18px", marginBottom:16, borderColor:`${T.green}33`, background:`${T.green}0F`, fontSize:13, color:T.green }}>
          {savedMsg}
        </div>
      )}

      {/* Investment summary */}
      {investment?.declared && (
        <div style={{ ...card, marginBottom:20, padding:"18px 22px", borderColor:`${T.cyan}26` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <h3 style={{ margin:0, fontSize:14, fontWeight:600 }}>Investment Declaration — {investment.financial_year}</h3>
            <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20,
              background: investment.regime==="New" ? `${T.cyan}1A` : `${T.amber}1A`,
              color:      investment.regime==="New" ? T.cyan : T.amber, fontWeight:600 }}>
              {investment.regime} Regime
            </span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
            {[
              { label:"Taxable Income", val:fmt(investment.total_taxable_income), color:T.amber },
              { label:"Annual Tax",     val:fmt(investment.total_tax),            color:T.red },
              { label:"Monthly TDS",    val:fmt(investment.monthly_tds),          color:T.green },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ background:T.surface_faint, borderRadius:8, padding:"12px 14px", border:`1px solid ${color}33` }}>
                <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>{label}</div>
                <div style={{ fontSize:18, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payslips list */}
      <div style={card}>
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.border}` }}>
          <h3 style={{ margin:0, fontSize:14, fontWeight:600 }}>My Payslips</h3>
        </div>

        {loading ? (
          <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>Loading…</div>
        ) : payslips.length === 0 ? (
          <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>No payslips yet</div>
        ) : (
          payslips.map(ps => (
            <div key={ps.id} style={{ padding:"14px 20px", borderBottom:`1px solid ${T.toggle_bg}`, display:"flex", alignItems:"center", gap:14 }}>
              {/* Icon */}
              <div style={{ width:40, height:40, borderRadius:10, background:`${T.green}1A`, display:"flex", alignItems:"center", justifyContent:"center", color:T.green, flexShrink:0 }}>
                <FileText size={18}/>
              </div>

              {/* Month + summary */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, fontFamily:"JetBrains Mono,monospace" }}>{ps.month}</div>
                <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
                  Gross: {fmt(ps.gross_salary)} · Deductions: {fmt(ps.total_deductions)}
                </div>
              </div>

              {/* Net pay */}
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <div style={{ fontSize:18, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color:T.green }}>{fmt(ps.net_salary)}</div>
                <div style={{ fontSize:11, color:T.muted }}>Net Pay</div>
              </div>

              {/* Status badge */}
              <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, fontWeight:600, flexShrink:0,
                background: ps.status==="Paid" ? `${T.green}1A` : `${T.amber}1A`,
                color:      ps.status==="Paid" ? T.green : T.amber }}>
                {ps.status}
              </span>

              {/* Action buttons */}
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                {/* View details (in-app modal) */}
                <button
                  onClick={() => { setSelSlip(ps); setSlipOpen(true); }}
                  style={{ fontSize:11.5, padding:"6px 11px", borderRadius:7, border:`1px solid ${T.cyan}33`, background:`${T.cyan}0F`, color:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif", display:"flex", alignItems:"center", gap:5 }}>
                  <Eye size={12}/> View
                </button>

                {/* View PDF in new tab */}
                <button
                  onClick={() => viewPDF(ps)}
                  disabled={downloading === `view-${ps.id}`}
                  title="Open PDF in new tab"
                  style={{ fontSize:11.5, padding:"6px 11px", borderRadius:7, border:`1px solid ${T.purple}33`, background:`${T.purple}0F`, color:T.purple, cursor:"pointer", fontFamily:"Outfit,sans-serif", display:"flex", alignItems:"center", gap:5, opacity: downloading===`view-${ps.id}` ? 0.6 : 1 }}>
                  <FileText size={12}/>
                  {downloading===`view-${ps.id}` ? "…" : "PDF"}
                </button>

                {/* Download PDF */}
                <button
                  onClick={() => downloadPDF(ps)}
                  disabled={downloading === ps.id}
                  title="Download payslip as PDF"
                  style={{ fontSize:11.5, padding:"6px 11px", borderRadius:7, border:`1px solid ${T.green}33`, background:`${T.green}0F`, color:T.green, cursor:"pointer", fontFamily:"Outfit,sans-serif", display:"flex", alignItems:"center", gap:5, opacity: downloading===ps.id ? 0.6 : 1 }}>
                  <Download size={12}/>
                  {downloading===ps.id ? "…" : "Download"}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Investment Declaration Modal ─────────────────────────── */}
      <Dialog open={invOpen} onClose={() => setInvOpen(false)} maxWidth="md">
        <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:640, fontFamily:"Outfit,sans-serif", overflow:"hidden", maxHeight:"90vh", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Investment Declaration — {CURR_FY}</h3>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={() => setInvOpen(false)}><X size={16}/></button>
          </div>
          <div style={{ padding:24, overflowY:"auto", display:"flex", flexDirection:"column", gap:16 }}>
            {/* Regime selector */}
            <div>
              <div style={{ fontSize:11.5, color:T.muted, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em" }}>Tax Regime</div>
              <div style={{ display:"flex", gap:10 }}>
                {["New","Old"].map(r => (
                  <button key={r} onClick={() => setForm(f=>({...f,regime:r}))}
                    style={{ flex:1, padding:"10px", borderRadius:8, border:`2px solid ${form.regime===r?T.cyan:T.border}`, background:form.regime===r?`${T.cyan}1A`:"transparent", color:form.regime===r?T.cyan:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600 }}>
                    {r} Regime {r==="New"?"(Default)":"(With Deductions)"}
                  </button>
                ))}
              </div>
            </div>

            {/* Deduction fields */}
            {(form.regime==="Old" ? INV_SECTIONS : INV_SECTIONS.filter(s=>s.key==="house_rent_paid")).map(section => (
              <div key={section.key} style={{ background:T.surface_faint, border:`1px solid ${section.color}33`, borderRadius:9, padding:"14px 16px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{section.label}</div>
                    <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{section.desc}</div>
                  </div>
                  {section.max && <div style={{ fontSize:11, color:section.color, background:`${section.color}26`, padding:"2px 8px", borderRadius:10 }}>Max ₹{section.max.toLocaleString()}</div>}
                </div>
                <input type="number" min={0} max={section.max||undefined}
                  value={form[section.key]}
                  onChange={e => setForm(f=>({...f,[section.key]:e.target.value}))}
                  placeholder="Enter amount (₹)"
                  style={{ width:"100%", background:T.inp_bg, border:`1px solid ${section.color}4D`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
              </div>
            ))}

            {form.regime==="New" && (
              <div style={{ background:`${T.cyan}0A`, border:`1px solid ${T.border}`, borderRadius:8, padding:"10px 14px", fontSize:12.5, color:T.muted }}>
                ℹ️ New Regime only allows HRA exemption and ₹50,000 standard deduction. For 80C/80D deductions, switch to Old Regime.
              </div>
            )}

            {/* Compute result */}
            {computeResult && (
              <div style={{ background:`${T.green}0F`, border:`1px solid ${T.green}26`, borderRadius:10, padding:"14px 18px", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                {[
                  { label:"Taxable Income", val:fmt(computeResult.taxable_income), color:T.amber },
                  { label:"Annual Tax",     val:fmt(computeResult.annual_tax),     color:T.red },
                  { label:"Monthly TDS",    val:fmt(computeResult.monthly_tds),    color:T.green },
                ].map(({label,val,color}) => (
                  <div key={label}>
                    <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>{label}</div>
                    <div style={{ fontSize:18, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
              <button onClick={handleCompute} disabled={computing}
                style={{ display:"inline-flex", alignItems:"center", gap:7, background:`${T.cyan}1A`, border:`1px solid ${T.cyan}33`, borderRadius:8, color:T.cyan, fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600, padding:"9px 16px", cursor:"pointer" }}>
                <Calculator size={13}/>{computing?"Computing…":"Compute Tax"}
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ display:"inline-flex", alignItems:"center", gap:7, background:`linear-gradient(135deg,${T.cyan},${T.purple})`, border:"none", borderRadius:8, color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600, padding:"9px 18px", cursor:"pointer" }}>
                <CheckCircle size={13}/>{saving?"Saving…":"Save Declaration"}
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Payslip Detail Modal ─────────────────────────────────── */}
      <Dialog open={slipOpen} onClose={() => setSlipOpen(false)} maxWidth="sm">
        {selSlip && (() => {
          const comps = getComps(selSlip);
          return (
            <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:520, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}>
              {/* Header */}
              <div style={{ padding:"20px 24px", background:`linear-gradient(135deg,${T.cyan}1A,${T.purple}1A)`, borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:3 }}>PAYSLIP</div>
                  <div style={{ fontSize:20, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color:T.cyan }}>{selSlip.month}</div>
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  {/* PDF buttons in modal */}
                  <button
                    onClick={() => viewPDF(selSlip)}
                    disabled={downloading===`view-${selSlip.id}`}
                    style={{ fontSize:12, padding:"6px 12px", borderRadius:7, border:`1px solid ${T.purple}4D`, background:`${T.purple}14`, color:T.purple, cursor:"pointer", fontFamily:"Outfit,sans-serif", display:"flex", alignItems:"center", gap:5, opacity: downloading===`view-${selSlip.id}` ? 0.6 : 1 }}>
                    <FileText size={12}/>{downloading===`view-${selSlip.id}` ? "Opening…" : "View PDF"}
                  </button>
                  <button
                    onClick={() => downloadPDF(selSlip)}
                    disabled={downloading===selSlip.id}
                    style={{ fontSize:12, padding:"6px 12px", borderRadius:7, border:`1px solid ${T.green}4D`, background:`${T.green}14`, color:T.green, cursor:"pointer", fontFamily:"Outfit,sans-serif", display:"flex", alignItems:"center", gap:5, opacity: downloading===selSlip.id ? 0.6 : 1 }}>
                    <Download size={12}/>{downloading===selSlip.id ? "Downloading…" : "Download PDF"}
                  </button>
                  <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={() => setSlipOpen(false)}>
                    <X size={16}/>
                  </button>
                </div>
              </div>

              {/* Earnings + Deductions */}
              <div style={{ padding:24, display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:T.green, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>Earnings</div>
                  {Object.entries(comps.earnings||{}).map(([k,v]) => (
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", marginBottom:7, fontSize:12.5 }}>
                      <span style={{ color:T.muted }}>{k}</span>
                      <span style={{ color:T.text, fontFamily:"JetBrains Mono,monospace" }}>{fmt(v)}</span>
                    </div>
                  ))}
                  <div style={{ display:"flex", justifyContent:"space-between", paddingTop:8, borderTop:`1px solid ${T.border}`, fontWeight:700, fontSize:13 }}>
                    <span>Gross</span>
                    <span style={{ color:T.amber, fontFamily:"JetBrains Mono,monospace" }}>{fmt(selSlip.gross_salary)}</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:T.red, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>Deductions</div>
                  {[["PF",selSlip.pf_employee],["ESI",selSlip.esi_employee],["Prof Tax",selSlip.professional_tax],["TDS",selSlip.tds]]
                    .filter(([,v]) => v > 0)
                    .map(([k,v]) => (
                      <div key={k} style={{ display:"flex", justifyContent:"space-between", marginBottom:7, fontSize:12.5 }}>
                        <span style={{ color:T.muted }}>{k}</span>
                        <span style={{ color:T.red, fontFamily:"JetBrains Mono,monospace" }}>-{fmt(v)}</span>
                      </div>
                    ))
                  }
                  <div style={{ display:"flex", justifyContent:"space-between", paddingTop:8, borderTop:`1px solid ${T.border}`, fontWeight:700, fontSize:13 }}>
                    <span>Total Deductions</span>
                    <span style={{ color:T.red, fontFamily:"JetBrains Mono,monospace" }}>-{fmt(selSlip.total_deductions)}</span>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div style={{ margin:"0 24px 20px", background:`linear-gradient(135deg,${T.green}14,${T.cyan}14)`, border:`1px solid ${T.green}26`, borderRadius:9, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ color:T.muted, fontSize:13 }}>Take-Home Pay</span>
                <span style={{ fontSize:20, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color:T.green }}>{fmt(selSlip.net_salary)}</span>
              </div>

              {/* LOP info if applicable */}
              {selSlip.lop_days > 0 && (
                <div style={{ margin:"0 24px 20px", background:`${T.amber}0F`, border:`1px solid ${T.amber}26`, borderRadius:9, padding:"10px 16px", fontSize:12.5, color:T.amber }}>
                  ⚠️ LOP: {selSlip.lop_days} day{selSlip.lop_days!==1?"s":""} deducted
                </div>
              )}
            </div>
          );
        })()}
      </Dialog>
    </div>
  );
}